// src/pages/container_rental/components/statusVisuals.ts
//
// 상태 → 색·패턴 단일 소스(single source of truth).
//
// 기존 시각 언어와의 정합성:
//  - RENTAL_VISUALS 의 accent/ko 는 StatusChip.tsx 의 STATUS_CONF 와 동일한
//    의미·색을 쓴다. (draft=먹, active=steel, paused=amber, closed=아웃라인,
//    cancelled=stop)
//  - ROUND_VISUALS 의 skipped/cancelled 는 RoundStatusChip.tsx 의 MUI color
//    매핑(warning/error)을 theme.ts 팔레트 값으로 옮긴 것이다.
//    warning=#E0A24A(amber), error=#B12B2B(stop).
//
// issued "면(fill)" 색 정본(확정, 설계서 §03): RentalCard ROUND DIAL 의
//   steel(#0E5C6F). 타임라인 세그먼트는 다이얼의 가로 확장이므로 면 채움은
//   steel 을 따른다. RoundStatusChip 의 issued=success(초록)는 "칩(텍스트
//   라벨)" 채널의 색으로, 면 채움과는 다른 채널이라 기존처럼 공존한다
//   (카드 뷰에서도 원래 다이얼=steel, 칩=초록으로 공존해 온 관례).
//
// 이 파일은 타임라인 전용이며 StatusChip / RoundStatusChip(둘 다 확정 디자인
// 시스템의 소프트 칩으로 교체 완료)과 "어긋나지 않게" 맞춰 둔 사본이다.

import { designTokens } from "../../../theme.ts";
import type {
  RentalStatus,
  RoundStatus,
} from "../../../contracts/container_rental/types";

const T = designTokens;

/**
 * 세그먼트가 깔리지 않은 바 바닥(트랙) 색.
 * 새 팔레트에서 FIELD(#F9FAFB)는 흰 서피스와 명도차가 거의 없어 면 채움
 * 신호가 사라진다 — 중립 톤(#F2F4F5)을 트랙 전용으로 쓴다.
 */
const TRACK_FILL = "#F2F4F5";

/* ------------------------------------------------------------------ */
/* 공유 SVG 패턴                                                       */
/* ------------------------------------------------------------------ */

export const PATTERN_ID = {
  /** paused 계약 바 전체를 덮는 앰버 빗금 */
  pausedHatch: "crt-hatch-paused",
  /** skipped 회차 — 앰버 빗금 */
  skippedHatch: "crt-hatch-skipped",
  /** cancelled 회차 — stop 빗금 */
  cancelledHatch: "crt-hatch-cancelled",
  /** 회차가 없는 추정 구간 — 옅은 먹 빗금 */
  ghostHatch: "crt-hatch-ghost",
} as const;

export interface HatchPatternSpec {
  id: string;
  /** 빗금 선 색 */
  color: string;
  /** 배경(없으면 투명) */
  bg?: string;
  /** 선 굵기(px) */
  strokeWidth: number;
  /** 패턴 타일 크기(px) */
  size: number;
  /** 회전 각(deg) */
  angle: number;
  opacity?: number;
}

/** TimelineDefs 가 문서에 딱 한 번 렌더한다. */
export const HATCH_PATTERNS: HatchPatternSpec[] = [
  { id: PATTERN_ID.pausedHatch,    color: T.amber, strokeWidth: 2,   size: 8, angle: 45, opacity: 0.9 },
  { id: PATTERN_ID.skippedHatch,   color: T.amber, strokeWidth: 2,   size: 6, angle: 45, bg: "#FDF6EA" },
  { id: PATTERN_ID.cancelledHatch, color: T.stop,  strokeWidth: 1.6, size: 6, angle: 45, bg: "#FBEFEF" },
  { id: PATTERN_ID.ghostHatch,     color: "#9A9DA6", strokeWidth: 1, size: 7, angle: 45, opacity: 0.7 },
];

/* ------------------------------------------------------------------ */
/* 계약 상태 (바 프레임 · 오버레이)                                    */
/* ------------------------------------------------------------------ */

export interface RentalVisual {
  /** StatusChip 과 동일한 한글 라벨 */
  ko: string;
  /** 상태 대표색 — 라벨 도트, 범례, KPI */
  accent: string;
  /** 바 프레임 선 색 */
  frameStroke: string;
  /** SVG stroke-dasharray. draft 만 점선. */
  frameDash?: string;
  /** 세그먼트가 깔리지 않는 바 바닥(트랙) 색 */
  trackFill: string;
  /** 바 전체를 덮는 오버레이 패턴 id (paused) */
  overlayPatternId?: string;
  /** 행 전체 불투명도 (cancelled 0.65) */
  rowOpacity: number;
  /** 바 끝을 "절단" 표식으로 마감할지 (closed) */
  truncatedEnd: boolean;
}

export const RENTAL_VISUALS: Record<RentalStatus, RentalVisual> = {
  draft: {
    ko: "초안",
    accent: "#1E2024",
    frameStroke: T.ink,
    frameDash: "4 3",
    trackFill: T.paper,
    rowOpacity: 0.9,
    truncatedEnd: false,
  },
  active: {
    ko: "진행중",
    accent: T.steel,
    frameStroke: T.ink,
    trackFill: TRACK_FILL,
    rowOpacity: 1,
    truncatedEnd: false,
  },
  paused: {
    ko: "일시정지",
    accent: T.amber,
    frameStroke: T.ink,
    trackFill: TRACK_FILL,
    overlayPatternId: PATTERN_ID.pausedHatch,
    rowOpacity: 1,
    truncatedEnd: false,
  },
  closed: {
    ko: "종료",
    accent: "#5C5F68",
    frameStroke: T.ink,
    trackFill: TRACK_FILL,
    rowOpacity: 0.85,
    truncatedEnd: true,
  },
  cancelled: {
    ko: "취소",
    accent: T.stop,
    frameStroke: T.stop,
    trackFill: T.paper,
    rowOpacity: 0.65,
    truncatedEnd: false,
  },
};

/* ------------------------------------------------------------------ */
/* 회차 상태 (세그먼트 채움 · 테두리 · 빗금)                            */
/* ------------------------------------------------------------------ */

export interface RoundVisual {
  /** RoundStatusChip 과 동일한 한글 라벨 */
  ko: string;
  /** 세그먼트 채움 — 색 또는 url(#pattern) */
  fill: string;
  stroke: string;
  strokeWidth: number;
  /** 세그먼트 위 회차번호 텍스트 색 */
  labelColor: string;
  /** 범례 등에서 쓰는 대표색(패턴이 아닌 실색) */
  accent: string;
}

export const ROUND_VISUALS: Record<RoundStatus, RoundVisual> = {
  scheduled: {
    ko: "예정",
    fill: T.paper,
    stroke: T.ink,
    strokeWidth: 1,
    labelColor: T.ink,
    accent: T.ink,
  },
  issued: {
    ko: "발행 완료",
    fill: T.steel,
    stroke: T.ink,
    strokeWidth: 1,
    labelColor: "#FFFFFF",
    accent: T.steel,
  },
  skipped: {
    ko: "건너뜀",
    fill: `url(#${PATTERN_ID.skippedHatch})`,
    stroke: T.amber,
    strokeWidth: 1,
    labelColor: "#8A5C12",
    accent: T.amber,
  },
  cancelled: {
    ko: "취소",
    fill: `url(#${PATTERN_ID.cancelledHatch})`,
    stroke: T.stop,
    strokeWidth: 1,
    labelColor: T.stop,
    accent: T.stop,
  },
};

/**
 * 연체 변형 — scheduled 세그먼트 중 서버가 overdue=true 로 표시한 것.
 * stop 2px 테두리 + 옅은 stop 틴트로 "예정인데 이미 지났다"를 표현한다.
 */
export const ROUND_OVERDUE_VISUAL: RoundVisual = {
  ko: "연체",
  fill: "#F6E2E2",
  stroke: T.stop,
  strokeWidth: 2,
  labelColor: T.stop,
  accent: T.stop,
};

/** 상태 + 연체 여부 → 세그먼트 시각 스펙. */
export function roundVisual(status: RoundStatus, overdue?: boolean): RoundVisual {
  if (overdue) return ROUND_OVERDUE_VISUAL;
  return ROUND_VISUALS[status] ?? ROUND_VISUALS.scheduled;
}

/** 계약 상태 → 바 시각 스펙(알 수 없는 값은 draft 취급). */
export function rentalVisual(status: RentalStatus): RentalVisual {
  return RENTAL_VISUALS[status] ?? RENTAL_VISUALS.draft;
}

/* ------------------------------------------------------------------ */
/* 부가 마킹 색 — 바 위에 얹는 표식들                                   */
/* ------------------------------------------------------------------ */

export const MARK = {
  /** 오늘선 */
  today: T.cargo,
  /** 1회차 INIT 언더라인 */
  init: T.amber,
  /** 발행 예정일 삼각 틱 */
  issueTick: T.ink,
  /** D-90 만료 예고 캡 */
  expiring: T.amber,
  /** 연체 */
  overdue: T.stop,
} as const;
